import {
	FC, useEffect, useMemo, useState,
} from 'react';
import {
	Contrast,
	ElasticProperty,
	InsideArrowsIcon,
	DashboardCellRendererFrame,
	NoValuePlaceholder,
} from '@aleph/shared-components';
import classNames from 'classnames';
import { XCircleIcon } from '@heroicons/react/solid';
import { RedditLevel, showXWithoutFocus } from 'wb-reddit/app/routes/superGrid/utils';
import SettingsBreakdownSelector from 'wb-reddit/app/routes/superGrid/cellRenderers/tool/settingsBreakdownSelector';
import { ICellRendererParams } from 'ag-grid-community';

const SettingsCellRenderer: FC<ICellRendererParams> = (params) => {
	const isDashboardView = params.data?.isDashboardView && !params.node.expanded;
	const { elasticProperty }: { elasticProperty: ElasticProperty<RedditLevel> } = params.context;

	const isLevelExpanded = elasticProperty?.levels?.includes?.(params.data?.level);
	const isLevelFocused = isLevelExpanded && params.api.getFocusedCell()?.rowIndex === params.rowIndex;

	// Calculation in order to show the X icon on lowest level when editing levels are not focused.
	const showNonFocusedX = useMemo(() => showXWithoutFocus(params, elasticProperty), [elasticProperty?.lastClicked]);

	const [isHovering, setIsHovering] = useState<boolean>(false);

	useEffect(() => {
		if (isLevelExpanded) {
			const rows = document.querySelectorAll(`div[row-id="${params.node.id}"]`);

			rows.forEach((node) => node.classList.add('row-editing'));
		}
	}, [isLevelExpanded, params.node.id]);

	return (
		<DashboardCellRendererFrame>
			<div
				className={classNames(
					'h-full w-full',
					!params?.data?.isCreate && {
						'bg-partner-50':  isLevelExpanded && params.data?.level === RedditLevel.AD_ACCOUNT,
						'bg-campaign-50': isLevelExpanded && params.data?.level === RedditLevel.CAMPAIGN,
						'bg-group-50':    isLevelExpanded && params.data?.level === RedditLevel.GROUP,
						'bg-ad-50':       isLevelExpanded && params.data?.level === RedditLevel.AD,
					},
				)
				}
				onMouseEnter={() => setIsHovering(true)}
				onMouseLeave={() => setIsHovering(false)}
			>
				<div
					className={
						classNames(
							'w-full flex justify-center h-[64px] max-h-full items-center',
							!params?.data?.isCreate && 'cursor-pointer',
						)
					}
				>
					{
						params?.data?.isCreate
							? <NoValuePlaceholder />
							: isLevelFocused || showNonFocusedX
								? <XCircleIcon width={16} />
								: isHovering && isLevelExpanded
									? <InsideArrowsIcon />
									: <Contrast width={16} />
					}
				</div>
			</div>

			{
				isDashboardView
				&& <SettingsBreakdownSelector
					{...params}
				/>
			}

			{
				isDashboardView
				&& <div>
					{new Array(3).fill(<div className="border-b border-dashed h-[32px] last:border-0" />)}
				</div>
			}

			{
				isDashboardView
				&& <div>
					{new Array(3).fill(<div className="border-b border-dashed h-[32px] last:border-0" />)}
				</div>
			}
		</DashboardCellRendererFrame>
	);
};

export default SettingsCellRenderer;
